import * as React from "react";
import { Input } from "@repo/ui";
import { DataTypeEnum, FormTypeEnum } from "@/services/entity.service";
import { DateTimePicker } from "@repo/ui";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { Switch } from "@repo/ui";
import { Textarea } from "@repo/ui";
import { RichTextEditor } from "../rich-text-editor/rich-text-editor";
import { JsonEditor } from "../json-editor";
import { SearchableSelect } from "./searchable-select";
import { WebsiteFavicon } from "../website-favicon";
import { ExternalLinkIcon, Mail, Phone, PhoneCall, Send } from "lucide-react";
import { Button } from "@repo/ui";
import { PhoneQRDialog } from "./phone-qr-dialog";
import { cn, validateField } from "@/lib/utils";
import { cleanPhone } from "@repo/utils/utils";
import { useEffect, useMemo, useRef } from "react";
import {
  getCurrencySymbol,
  percentDisplayToFraction,
  percentFractionToDisplay,
} from "@/lib/field-formatter";

export interface InputByScalarProps extends React.InputHTMLAttributes<
  HTMLInputElement | HTMLTextAreaElement
> {
  fieldType: DataTypeEnum;
  formType?: FormTypeEnum;
  formatCurrency?: string | null;
  formatLocale?: string | null;
  validationString?: string;
  showValidationErrors?: boolean;
  /**
   * Render in a single-row height suited to compact horizontal contexts (e.g.
   * the multi-edit dock). Currently only affects LONGTEXT, which otherwise
   * renders a 4-row textarea that overflows a short bar.
   */
  compact?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onValidityChange?: (name: string, isValid: boolean) => void;
  enumValues?: Array<{ id: string; value: string }>;
  /** RICHTEXT: controlled full-screen state, for callers that place the expand trigger. */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export const InputByFieldType = React.forwardRef<HTMLInputElement, InputByScalarProps>(
  // ref is not used but react compains if we dont use it
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (
    {
      fieldType: scalarType,
      formType,
      formatCurrency,
      formatLocale,
      validationString,
      showValidationErrors: showValidationErrors = true,
      onValidityChange,
      enumValues,
      compact,
      expanded,
      onExpandedChange,
      ...props
    },
    ref,
  ) => {
    const value = props.value?.toString() ?? "";

    // Auto-grow the compact LONGTEXT textarea with its content (capped by
    // max-height in CSS) so the multi-edit dock grows instead of overflowing.
    const compactTextareaRef = useRef<HTMLTextAreaElement>(null);
    useEffect(() => {
      if (!compact) return;
      const el = compactTextareaRef.current;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }, [compact, value]);

    const validationResult = useMemo(() => {
      if (!props.name) {
        return { isValid: true, errors: [] };
      }

      const { isValid, errors } = validateField(value, validationString);

      return { isValid, errors };
    }, [value, validationString, props.name]);

    // Debounce validation visibility: hide while typing; validate after short idle
    const [showValidation, setShowValidation] = React.useState(false);

    // Track previous value of showValidationErrors to detect changes
    const prevShowValidationErrors = useRef(showValidationErrors);

    // compouted value to so we can override the local show validation errors state
    const displayValidation = React.useMemo(() => {
      return showValidation && showValidationErrors;
    }, [showValidation, showValidationErrors]);

    useEffect(() => {
      const fieldName = props.name ?? "";
      if (!fieldName) return;

      // Always report actual validity immediately - this is separate from visual display
      onValidityChange?.(fieldName, validationResult.isValid);

      if (!showValidationErrors) {
        setShowValidation(false);
        prevShowValidationErrors.current = showValidationErrors;
        return;
      }

      // Check if showValidationErrors changed from false to true
      const changedFromFalseToTrue = !prevShowValidationErrors.current && showValidationErrors;

      if (changedFromFalseToTrue) {
        // Show validation immediately when switching from false to true
        setShowValidation(true);
      } else {
        // Use debounced validation for other cases (typing, etc.)
        setShowValidation(false);
        const timer = setTimeout(() => {
          setShowValidation(true);
        }, 500);

        // Update the ref after setting up the timer
        prevShowValidationErrors.current = showValidationErrors;
        return () => clearTimeout(timer);
      }

      // Update the ref for immediate validation case
      prevShowValidationErrors.current = showValidationErrors;
    }, [
      value,
      validationString,
      showValidationErrors,
      props.name,
      onValidityChange,
      validationResult.isValid,
    ]);

    const { isValid, errors } = validationResult;
    const shouldShowError = displayValidation && !isValid && !!validationString;

    switch (scalarType.toUpperCase() as DataTypeEnum) {
      case "INT":
        return (
          <div className="relative">
            <Input
              type="number"
              inputMode="numeric"
              step={1}
              pattern="\\d*"
              className={cn({
                "border-red-500 focus:!border-red-500": shouldShowError,
              })}
              onKeyDown={(e) => {
                // Block decimal separators and exponent notation for integers
                if (e.key === "." || e.key === "," || e.key.toLowerCase() === "e") {
                  e.preventDefault();
                }
                props.onKeyDown?.(e);
              }}
              onPaste={(e) => {
                // Sanitize pasted content to digits (and optional leading minus)
                e.preventDefault();
                const text = (e.clipboardData || (window as any).clipboardData).getData("text");
                const sanitized = String(text).replace(/[^\d-]/g, "");
                props.onChange?.({
                  target: { value: sanitized },
                } as React.ChangeEvent<HTMLInputElement>);
              }}
              onChange={(e) => {
                const next = String(e.target.value ?? "").replace(/[.,]/g, "");
                props.onChange?.({
                  ...e,
                  target: { ...e.target, value: next },
                });
              }}
              {...props}
            />
            {shouldShowError && errors.length > 0 && (
              <div className="absolute left-0 top-full mt-1 text-sm text-red-500">
                {errors.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </div>
            )}
          </div>
        );
      case "FLOAT":
        if (formType === "PERCENT") {
          const rawFraction =
            props.value === null || props.value === undefined || props.value === ""
              ? ""
              : String(props.value);
          const displayValue = percentFractionToDisplay(rawFraction);
          return (
            <div className="group relative">
              <Input
                {...props}
                type="number"
                step="any"
                placeholder={props.placeholder ?? "0"}
                value={displayValue}
                onChange={(e) => {
                  const next = percentDisplayToFraction(e.target.value);
                  props.onChange?.({
                    ...e,
                    target: { ...e.target, value: next },
                  });
                }}
                className={cn({
                  "pr-8": true,
                  "border-red-500 focus:!border-red-500": shouldShowError,
                })}
              />
              <span
                className={cn("absolute right-3 top-1/2 -translate-y-1/2 select-none text-sm", {
                  "text-gray-500": !props.value,
                  "text-red-500": shouldShowError,
                })}
              >
                %
              </span>
              {shouldShowError && errors.length > 0 && (
                <div className="absolute left-0 top-full mt-1 text-sm text-red-500">
                  {errors.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              )}
            </div>
          );
        }
        if (formType === "CURRENCY") {
          const currencySymbol = getCurrencySymbol(formatCurrency, formatLocale);
          return (
            <div className="group relative">
              <span
                className={cn("absolute left-3 top-1/2 -translate-y-1/2 select-none text-sm", {
                  "text-gray-500": !props.value,
                  "text-red-500": shouldShowError,
                })}
              >
                {currencySymbol}
              </span>
              <Input
                type="number"
                {...props}
                placeholder="0,00"
                step="any"
                className={cn({
                  "pl-8": true,
                  "border-red-500 focus:!border-red-500": shouldShowError,
                })}
              />
              {shouldShowError && errors.length > 0 && (
                <div className="absolute left-0 top-full mt-1 text-sm text-red-500">
                  {errors.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              )}
            </div>
          );
        }
        return (
          <div className="relative">
            <Input
              type="number"
              {...props}
              step="any"
              placeholder={props.placeholder}
              className={cn({
                "border-red-500 focus:!border-red-500": shouldShowError,
              })}
            />
            {shouldShowError && errors.length > 0 && (
              <div className="absolute left-0 top-full mt-1 text-sm text-red-500">
                {errors.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </div>
            )}
          </div>
        );
      case "DATE":
        return (
          <DateTimePicker
            granularity="day"
            locale={de}
            clearable
            {...props}
            value={props.value ? parseISO(String(props.value)) : undefined}
            onChange={(e) =>
              props.onChange?.({
                target: {
                  value: e ? format(e, "yyyy-MM-dd") : "",
                },
              } as React.ChangeEvent<HTMLInputElement>)
            }
          />
        );
      case "DATETIME":
        return (
          <DateTimePicker
            clearable
            {...props}
            value={props.value ? new Date(String(props.value)) : undefined}
            locale={de}
            onChange={(e) =>
              props.onChange?.({
                target: {
                  value: e?.toISOString() ?? "",
                },
              } as React.ChangeEvent<HTMLInputElement>)
            }
          />
        );

      case "TIME": {
        // Convert UTC to local time for display
        const timeValue = props.value
          ? new Date(String(props.value)).toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
            })
          : "";
        return (
          <Input
            type="time"
            step="1"
            {...props}
            value={timeValue}
            onChange={(e) => {
              // Convert local time input to UTC
              const [hours, minutes, seconds] = (e.target.value || "00:00:00").split(":");
              const date = new Date();
              date.setHours(+hours, +minutes, +(seconds || 0), 0);
              props.onChange?.({
                target: { value: date.toISOString() },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
          />
        );
      }
      case "BOOLEAN":
        return (
          <Switch
            checked={!!props.value}
            onCheckedChange={(e) => {
              props.onChange?.({
                target: {
                  value: !!e,
                },
              } as unknown as React.ChangeEvent<HTMLInputElement>);
            }}
            // {...props}
          />
        );
      case "LONGTEXT":
        return (
          <div className="relative">
            {/* @ts-expect-error  props dont match */}
            <Textarea
              {...props}
              ref={compact ? compactTextareaRef : undefined}
              rows={compact ? 1 : 4}
              className={cn(
                "field-focus focus-visible:outline-none",
                props.className,
                // compact: auto-grow with content from a comfortable base, capped
                // (then scroll), so the multi-edit composer card stays tidy.
                compact && "min-h-[72px] max-h-48 resize-none overflow-y-auto",
                {
                  "border-red-500 focus-visible:!border-red-500 focus-visible:!ring-0 active:!border-red-500 [&:focus]:!border-red-500 [&:focus]:!ring-0":
                    shouldShowError,
                },
              )}
              // this is somehow needed to get the red border to work
              style={
                shouldShowError
                  ? {
                      borderColor: "#ef4444 !important",
                      boxShadow: "none",
                      outline: "none",
                    }
                  : undefined
              }
            />
            {shouldShowError && errors.length > 0 && (
              <div className="absolute left-0 top-full mt-1 text-xs text-red-500">
                {errors.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </div>
            )}
          </div>
        );
      case "RICHTEXT":
        return (
          <RichTextEditor
            content={props.value as string}
            onChange={(e) => {
              props.onChange?.({
                target: {
                  value: e,
                },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
            expandable
            expandTitle={props.name || "Edit Content"}
            expanded={expanded}
            onExpandedChange={onExpandedChange}
          />
        );
      case "JSON":
        return (
          <JsonEditor
            value={props.value as string}
            onChange={(e) => {
              props.onChange?.({
                target: {
                  value: e,
                },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
          />
        );
      case "ENUM":
        return (
          <SearchableSelect
            options={enumValues?.map((ev) => ({ value: ev.value, label: ev.value })) ?? []}
            value={value}
            onValueChange={(val) => {
              props.onChange?.({
                target: { value: val },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
            placeholder="Select..."
            searchPlaceholder="Search options..."
            emptyMessage="No options found."
            disabled={props.disabled}
          />
        );
      case "TEXT":
        // Special handling for PRICE moved to FLOAT case

        const isWebsiteValue = formType === "WEBSITE";
        const isEmailValue = formType === "EMAIL";
        const isPhoneValue = formType === "PHONE";

        return (
          <div
            className={cn("group relative", {
              "h-10": isEmailValue,
            })}
          >
            {isWebsiteValue && props.value && props.value.toString().startsWith("http") && (
              <WebsiteFavicon
                className={cn("absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2", {
                  "text-gray-500": !props.value,
                  "text-red-500": shouldShowError,
                })}
                url={props.value as string}
              />
            )}
            {isEmailValue && (
              <Mail
                className={cn("absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2", {
                  "text-red-500": shouldShowError,
                  "text-gray-500": !props.value,
                })}
              />
            )}
            {isPhoneValue && (
              <Phone
                className={cn("absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2", {
                  "text-gray-500": !props.value,
                  "text-red-500": shouldShowError,
                })}
              />
            )}
            <Input
              type="text"
              className={cn({
                "pl-8": isWebsiteValue || isEmailValue || isPhoneValue,
                "border-red-500 focus:!border-red-500": shouldShowError,
              })}
              {...props}
            />
            {isWebsiteValue && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 hidden h-6 w-6 -translate-y-1/2 place-items-center hover:bg-gray-100 group-hover:grid"
                asChild
              >
                <a
                  href={props.value as string}
                  target="_blank"
                  rel="noreferrer"
                  className={!isValid || !props.value ? "pointer-events-none opacity-50" : ""}
                >
                  <ExternalLinkIcon className="h-4 w-4" />
                </a>
              </Button>
            )}
            {isEmailValue && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 hidden h-6 w-6 -translate-y-1/2 place-items-center hover:bg-gray-100 group-hover:grid"
                asChild
              >
                <a
                  href={`mailto:${props.value}`}
                  target="_blank"
                  rel="noreferrer"
                  className={!isValid || !props.value ? "pointer-events-none opacity-50" : ""}
                >
                  <Send className="mr-0.5 mt-0.5 h-4 w-4" />
                </a>
              </Button>
            )}
            {isPhoneValue && props.value && (
              <PhoneQRDialog phoneNumber={cleanPhone(props.value?.toString() ?? "")}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 hidden h-6 w-6 -translate-y-1/2 place-items-center hover:bg-gray-100 group-hover:grid"
                >
                  <PhoneCall className="h-4 w-4" />
                </Button>
              </PhoneQRDialog>
            )}
            {shouldShowError && errors.length > 0 && (
              <div className="pointer-events-none absolute left-0 top-full z-10 mt-0.5 text-xs text-red-500">
                {errors.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </div>
            )}
          </div>
        );

      case "SEQUENCE":
        return (
          <Input
            type="text"
            className={cn("font-mono", props.className)}
            placeholder="Assigned on save"
            {...props}
          />
        );

      default:
        return (
          <div className="relative">
            <Input
              type="text"
              className={cn({
                "border-red-500 focus:!border-red-500": shouldShowError,
              })}
              {...props}
            />
            {shouldShowError && errors.length > 0 && (
              <div className="absolute left-0 top-full mt-1 text-sm text-red-500">
                {errors.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </div>
            )}
          </div>
        );
    }
  },
);
InputByFieldType.displayName = "InputByFieldType";
